import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { bindActionCreators } from 'redux';

import CircleLoader from 'ggApp/shared/components/CircleLoader';
import ggStore from 'ggApp/modules/store';
import ggAuth from 'ggApp/modules/auth';
import ggCountry from 'ggApp/modules/country';
import {
    getAllDeviceFeatureFlags as getAllDeviceFeatureFlagsAction,
    getAllUserFeatureFlags as getAllUserFeatureFlagsAction,
} from 'ggApp/modules/featureFlag';
import { inlineOrderNumber, storeCode } from 'ggApp/modules/router/selectors';
import { list as listSelector } from 'ggApp/modules/category/selectors';
import { getCategories as getCategoriesAction } from 'ggApp/modules/category/actions';
import { INIT_DONE } from 'ggApp/modules/init/actionTypes';
import { OverwriteOrder } from 'ggApp/shared/hocs/router/overwriteOrder';

import ErrorPage from 'ggApp/shared/screens/ErrorPage';

// i18next translations
import { initLocalization } from 'ggApp/modules/translations';

import AppShell from './AppShell';

// global styles for app
import 'reset-css/reset.css';
import '../styles/app.scss';
import '../../cart/_wrap/index.scss';

@connect(
    createStructuredSelector({
        user: ggAuth.selectors.user,
        storeActive: ggStore.selectors.storeActive,
        isStoreLoading: ggStore.selectors.isStoreLoading,
        failedToLoadStore: ggStore.selectors.failedToLoadStore,
        isStoreReady: ggStore.selectors.isStoreReady,
        inlineOrderNumber,
        storeCode,
        categoryList: listSelector,
    }),
    (dispatch) => ({
        matchStore: bindActionCreators(ggStore.actions.matchStore, dispatch),
        getCountries: bindActionCreators(ggCountry.actions.getCountries, dispatch),
        getCategories: bindActionCreators(getCategoriesAction, dispatch),
        getAllDeviceFeatureFlags: bindActionCreators(getAllDeviceFeatureFlagsAction, dispatch),
        getAllUserFeatureFlags: bindActionCreators(getAllUserFeatureFlagsAction, dispatch),
        dispatch,
    }),
)
@OverwriteOrder
export default class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDependenciesResolved: false,
        };
    }

    componentDidMount() {
        const {
            matchStore,
            getCountries,
            getCategories,
            getAllDeviceFeatureFlags,
            getAllUserFeatureFlags,
            dispatch,
        } = this.props;

        matchStore(initLocalization)
            .catch(({ response: error = {} }) => {
                if (error && error.code === 404) {
                    throw new Error(`
                        Could not fetch the store with code '${error.storeCodeParam}'
                    `);
                }

                // If something else happened, re-throw Sentry to catch
                throw error;
            })
            .then(() =>
                Promise.all([
                    getCountries(),
                    getCategories(),
                    getAllDeviceFeatureFlags(),
                    getAllUserFeatureFlags(),
                ]),
            )
            .finally(() => {
                this.setState({
                    isDependenciesResolved: true,
                });
                dispatch({ type: INIT_DONE });
            });
    }

    componentDidUpdate(prevProps) {
        const { user, getAllUserFeatureFlags } = this.props;
        const { country_code: countryCode } = user.default_store || {};
        if (user?.id && prevProps?.user?.id !== user?.id && countryCode) {
            getAllUserFeatureFlags();
        }
    }

    render() {
        const { children, user, isStoreLoading, failedToLoadStore, isStoreReady } = this.props;
        const { isDependenciesResolved } = this.state;
        const isAppLoading = isStoreLoading || user === null || !isDependenciesResolved;

        if (failedToLoadStore) {
            return (
                <AppShell>
                    <ErrorPage />
                </AppShell>
            );
        }

        if (isAppLoading || !isStoreReady) {
            return (
                <AppShell>
                    <CircleLoader />
                </AppShell>
            );
        }

        return <AppShell>{children}</AppShell>;
    }
}
